import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePrevious } from 'react-use';
import { useRobotOperations } from '../../../../../../api/hooks/binding/robot-operations.binding';
import { ProgramState } from '../../../../../../components/machine/machine-state';
import { useMachineContext } from '../../../../../../pages/machine/machine-context-provider';
import { logger } from '../../../../../../util/logger';
import { Pose } from '../../../../../../util/pose.util';
import { ToolConfig } from '../../../model/flow.types';

interface FineTuneScriptProps {
  onProgramStop?: () => void;
  toolConfig: ToolConfig;
  executingIndex: number;
}

interface FineTuneScriptResponse {
  moveToPosition: (pose: Pose) => Promise<void>;
}

interface FineTuneScriptLazyResponse {
  executeFineTune: () => Promise<boolean>;
  moveToPosition: (pose: Pose) => Promise<void>;
  isReady: boolean;
  close: () => void;
}

const useFineTuneScript = (
  props: FineTuneScriptProps
): FineTuneScriptResponse => {
  const { executeFineTune, moveToPosition } = useFineTuneScriptLazy(props);

  useEffect(() => {
    // Automatically execute the fine tune script on mount
    executeFineTune();
  }, [executeFineTune]);

  const response = useMemo<FineTuneScriptResponse>(() => {
    return {
      moveToPosition,
    };
  }, [moveToPosition]);

  return response;
};

/**
 * Lazy version of useFineTuneScript that only executes the script when explicitly called.
 * This is useful when you want to control when the fine tune script starts (e.g., on button press).
 */
const useFineTuneScriptLazy = (
  props: FineTuneScriptProps
): FineTuneScriptLazyResponse => {
  const { onProgramStop, toolConfig, executingIndex } = props;

  const { machine } = useMachineContext();
  const robotOperations = useRobotOperations();
  const { startFineTune, fineTuneMoveTo } = robotOperations;
  const [scriptExecutionFailed, setScriptExecutionFailed] =
    useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const hasSetupScript = useRef<boolean>(false);

  const programState = machine.state?.transient?.status?.programState;
  const previousProgramState = usePrevious(programState);

  // Handle program stop events
  useEffect(() => {
    if (!scriptExecutionFailed) {
      if (
        [ProgramState.PROGRAM_PLAYING, ProgramState.PROGRAM_PAUSED].includes(
          previousProgramState
        ) &&
        programState === ProgramState.PROGRAM_STOPPED
      ) {
        if (typeof onProgramStop === 'function') {
          onProgramStop();
        }
        // Reset the script setup flag so it can be executed again
        hasSetupScript.current = false;
        setIsReady(false);
      }
    }
  }, [
    previousProgramState,
    programState,
    scriptExecutionFailed,
    onProgramStop,
  ]);

  const executeFineTune = useCallback(async (): Promise<boolean> => {
    const currentProgramState =
      machine.state?.transient?.status?.programState ??
      ProgramState.PROGRAM_STOPPED;

    const shouldSendFineTuneScript = [
      ProgramState.PROGRAM_STOPPED,
      ProgramState.PROGRAM_STOPPING,
    ].includes(currentProgramState);

    if (hasSetupScript.current || !shouldSendFineTuneScript) {
      return false;
    }

    if (!toolConfig) {
      return false;
    }

    try {
      setScriptExecutionFailed(false);

      const result = await startFineTune(machine.id, {
        toolConfig,
        executingIndex,
        onStop: onProgramStop,
      });

      if (result?.success) {
        hasSetupScript.current = true;
        setIsReady(true);
        return true;
      } else {
        hasSetupScript.current = false;
        setScriptExecutionFailed(true);
        setIsReady(false);
        return false;
      }
    } catch (error) {
      hasSetupScript.current = false;
      setScriptExecutionFailed(true);
      setIsReady(false);
      return false;
    }
  }, [
    machine.state?.transient?.status?.programState,
    machine.id,
    toolConfig,
    startFineTune,
    executingIndex,
    onProgramStop,
  ]);

  const moveToPosition = useCallback(
    async (pose1: Pose) => {
      const result = await fineTuneMoveTo(machine.id, pose1);

      if (!result?.success) {
        logger.error('Failed to move to position during fine-tune:', {
          error: result?.error,
        });
      }
    },
    [fineTuneMoveTo, machine?.id]
  );

  const close = useCallback(() => {
    // Reset internal state before closing the binding
    hasSetupScript.current = false;
    setIsReady(false);
    setScriptExecutionFailed(false);

    // Close the robot operations binding
    robotOperations.close();
  }, [robotOperations]);

  const response = useMemo<FineTuneScriptLazyResponse>(() => {
    return {
      executeFineTune,
      moveToPosition,
      isReady,
      close,
    };
  }, [executeFineTune, moveToPosition, isReady, close]);

  return response;
};

export { useFineTuneScript, useFineTuneScriptLazy };
